import {
  estimatePaceSecPerKm,
  estimatedDurationSecFromDistance,
  formatDistanceFromMeters,
  metersForSegmentDistanceLine,
} from './runningSegmentLabels';

/**
 * Duration (s) used for a split: primary duration, or estimated from distance when distance-primary.
 * Distance-primary segments use the same pace model as the split table (not stale stored duration_sec).
 */
export function segmentEffectiveDurationSec(seg, thresholdPaceSecPerKm, paceEstimateOpts = {}) {
  const mode = (seg.split_measure_mode || 'duration').toLowerCase() === 'distance' ? 'distance' : 'duration';
  if (mode === 'duration') {
    const d = seg.duration_sec === '' || seg.duration_sec == null ? 0 : Number(seg.duration_sec) || 0;
    return Math.max(0, Math.round(d));
  }
  const dm = seg.distance_m === '' || seg.distance_m == null ? NaN : Number(seg.distance_m);
  if (Number.isFinite(dm) && dm > 0) {
    const pace = estimatePaceSecPerKm(seg, thresholdPaceSecPerKm, paceEstimateOpts);
    if (pace != null && pace > 0) {
      return Math.max(0, Math.round((dm / 1000) * pace));
    }
  }
  const stored = seg.duration_sec === '' || seg.duration_sec == null ? 0 : Number(seg.duration_sec) || 0;
  if (stored > 0) return Math.max(0, Math.round(stored));
  const est = estimatedDurationSecFromDistance(seg, thresholdPaceSecPerKm, paceEstimateOpts);
  return est != null && est > 0 ? est : 0;
}

/**
 * %CP for summaries: stored value, else midpoint of range, else defaults (0 for rest).
 */
export function segmentDisplayCpPercent(seg) {
  const raw = seg.cp_percent;
  const n = raw === '' || raw == null ? NaN : Number(String(raw).trim().replace(',', '.'));
  if (Number.isFinite(n)) return n;
  const lo = Number(seg.cp_percent_min);
  const hi = Number(seg.cp_percent_max);
  if (Number.isFinite(lo) && Number.isFinite(hi)) return (lo + hi) / 2;
  if (Number.isFinite(lo)) return lo;
  if (Number.isFinite(hi)) return hi;
  const ic = (seg.run_intensity_class || 'work').toLowerCase();
  if (ic === 'rest') return 0;
  return 75;
}

function formatTotalDurationSec(sec) {
  const s = Math.round(Number(sec) || 0);
  if (s <= 0) return null;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rs = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(rs).padStart(2, '0')}`;
  return `${m}:${String(rs).padStart(2, '0')}`;
}

const DASH = '—';

/**
 * @param {object[]} segments — editor rows
 * @param {number|null|undefined} thresholdPaceSecPerKm
 * @param {'km'|'miles'} distanceUnits — Settings display preference
 * @param {(k: string, o?: object) => string} t
 */
export function computeRunningWorkoutEditorSummary(
  segments,
  thresholdPaceSecPerKm,
  distanceUnits,
  t,
  paceEstimateOpts = {}
) {
  if (!Array.isArray(segments) || segments.length === 0) {
    return {
      distanceLabel: DASH,
      timeLabel: DASH,
      sufferScoreLabel: DASH,
      weightedCpLabel: DASH,
      totalMeters: 0,
      totalSec: 0,
      weightedCp: null,
      sufferScoreEstimate: null,
    };
  }

  let totalM = 0;
  let totalSec = 0;
  let wCp = 0;
  let wDur = 0;

  for (const seg of segments) {
    const m = metersForSegmentDistanceLine(seg, thresholdPaceSecPerKm, paceEstimateOpts);
    if (m != null && Number.isFinite(m) && m > 0) totalM += m;

    const dur = segmentEffectiveDurationSec(seg, thresholdPaceSecPerKm, paceEstimateOpts);
    totalSec += dur;

    if (dur > 0) {
      const cp = segmentDisplayCpPercent(seg);
      if (Number.isFinite(cp)) {
        wCp += cp * dur;
        wDur += dur;
      }
    }
  }

  const du = distanceUnits === 'miles' ? 'miles' : 'km';
  const distanceLabel =
    totalM > 0 ? formatDistanceFromMeters(totalM, du, t) || DASH : DASH;

  const timeLabel = formatTotalDurationSec(totalSec) || DASH;

  const weightedCp = wDur > 0 ? wCp / wDur : null;
  const weightedCpLabel =
    weightedCp != null && Number.isFinite(weightedCp)
      ? t('runningExercise.editorSummaryWeightedCpValue', { value: weightedCp.toFixed(1) })
      : DASH;

  /** Strava-style load proxy from planned time + %CP (no HR); same ballpark as “Suffer” for comparison only. */
  let sufferScoreEstimate = null;
  if (totalSec > 0 && weightedCp != null && Number.isFinite(weightedCp)) {
    const minutes = totalSec / 60;
    const cp = Math.max(35, Math.min(150, weightedCp));
    sufferScoreEstimate = Math.max(0, Math.round(minutes * Math.pow(cp / 75, 1.25) * 0.9));
  }
  const sufferScoreLabel = sufferScoreEstimate != null ? String(sufferScoreEstimate) : DASH;

  return {
    distanceLabel,
    timeLabel,
    sufferScoreLabel,
    weightedCpLabel,
    totalMeters: totalM,
    totalSec,
    weightedCp,
    sufferScoreEstimate,
  };
}
